import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2, ChevronLeft, ChevronRight, Building2 } from "lucide-react";
import { def1, def2, def3, def4, def5, def6, def7, def8, def9 } from "../../assets/images";

const Hero = () => {
  const { t } = useTranslation();
  const [key, setKey] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sectionRef = useRef(null);

  const slides = [def1, def2, def3, def4, def5, def6, def7, def8, def9];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setKey(prev => prev + 1); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  // Auto-slide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const scrollTo = (id, e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <section
      id="home"
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentSlide}
            src={slides[currentSlide]}
            alt={`AUXIS - Réalisation ${currentSlide + 1}`}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-auxis-charcoal/90 via-auxis-charcoal/70 to-auxis-charcoal/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-auxis-charcoal via-transparent to-auxis-charcoal/30" />
      </div>

      {/* Content */}
      <div className="container-wide relative z-10 pt-32 pb-32 md:pt-40 md:pb-40">
        <div className="max-w-3xl">
          {/* Badge */}
          <motion.div
            key={`badge-${key}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-xs font-semibold uppercase tracking-widest rounded-full border border-white/20">
              <span className="w-1.5 h-1.5 rounded-full bg-auxis-gold animate-pulse" />
              {t('hero.badge')}
            </span>
          </motion.div>

          {/* Main heading */}
          <motion.h1
            key={`title-${key}`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-[1.1] tracking-tight"
          >
            {t('hero.title1')}
            <br />
            <span className="text-auxis-gold">{t('hero.title2')}</span>
          </motion.h1>

          {/* Description */}
          <motion.p
            key={`desc-${key}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="text-lg md:text-xl text-white/80 max-w-2xl mb-8 leading-relaxed"
          >
            {t('hero.desc')}
          </motion.p>

          {/* Key Points */}
          <motion.div
            key={`points-${key}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap gap-x-6 gap-y-3 mb-10"
          >
            {[t('hero.point1'), t('hero.point2'), t('hero.point3'), t('hero.point4')].map((point, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-white/90">
                <CheckCircle2 size={16} className="text-auxis-gold flex-shrink-0" />
                <span>{point}</span>
              </div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            key={`cta-${key}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <motion.a
               href="#contact"
               onClick={(e) => scrollTo('contact', e)}
               className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-auxis-gold text-auxis-charcoal font-semibold rounded-xl hover:bg-yellow-500 transition-all duration-300 shadow-lg shadow-auxis-gold/30"
               whileHover={{ scale: 1.02, y: -2 }}
               whileTap={{ scale: 0.98 }}
            >
               {t('hero.btnQuote')}
               <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </motion.a>
            <motion.a
               href="#services"
               onClick={(e) => scrollTo('services', e)}
               className="inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300"
               whileHover={{ scale: 1.02, y: -2 }}
               whileTap={{ scale: 0.98 }}
            >
               {t('hero.btnServices')}
            </motion.a>
          </motion.div>
        </div>
      </div>

      {/* Slide Navigation */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 z-20 hidden md:flex flex-col gap-3">
        <button
          onClick={prevSlide}
          className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-colors duration-300 border border-white/20"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={nextSlide}
          className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-colors duration-300 border border-white/20"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentSlide(i)}
            className={`h-1 rounded-full transition-all duration-500 ${
              i === currentSlide
                ? 'bg-auxis-gold w-8'
                : 'bg-white/40 w-4 hover:bg-white/60'
            }`}
          />
        ))}
      </div>

      {/* Bottom Stats Bar */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="absolute bottom-0 left-0 right-0 bg-auxis-charcoal/80 backdrop-blur-md border-t border-white/10"
      >
        <div className="container-wide py-5">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-6 md:gap-12 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-auxis-gold/20 flex items-center justify-center">
                <Building2 size={18} className="text-auxis-gold" />
              </div>
              <div>
                <div className="text-base font-bold">{t('hero.stat1Title')}</div>
                <div className="text-xs text-gray-400">{t('hero.stat1Desc')}</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-10 bg-white/20" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-auxis-gold/20 flex items-center justify-center">
                <Building2 size={18} className="text-auxis-gold" />
              </div>
              <div>
                <div className="text-base font-bold">{t('hero.stat2Title')}</div>
                <div className="text-xs text-gray-400">{t('hero.stat2Desc')}</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-10 bg-white/20" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-auxis-gold/20 flex items-center justify-center">
                <Building2 size={18} className="text-auxis-gold" />
              </div>
              <div>
                <div className="text-base font-bold">{t('hero.stat3Title')}</div>
                <div className="text-xs text-gray-400">{t('hero.stat3Desc')}</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
